// src/services/api.js - COMPLETE UPDATED VERSION
import axios from 'axios';

// For local development
const API_URL = import.meta.env.VITE_API_URL || "http://localhost:5000/api/v1";

// For production
// const API_URL = import.meta.env.VITE_API_URL || "https://diredawa-university-material-repository.onrender.com/api/v1";
// Create axios instance
// import axios from 'axios';

// const API_URL = import.meta.env.VITE_API_URL || "https://diredawa-university-material-repository.onrender.com/api/v1";

// Create axios instance
const api = axios.create({
  baseURL: API_URL
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle responses
api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.response?.status === 401 && !error.config.url.includes('/auth/login')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error.response?.data || error.message);
  }
);

// ==========================================
// Helper Functions
// ==========================================

export const getFileCategory = (fileFormat, mimeType) => {
  const format = (fileFormat || '').toLowerCase().replace('.', '');
  const mime = (mimeType || '').toLowerCase();
  
  if (mime.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'avif', 'ico', 'tiff'].includes(format)) {
    return 'image';
  }
  if (mime.startsWith('video/') || ['mp4', 'm4v', 'webm', 'ogv', 'mov', 'mkv', 'avi', '3gp'].includes(format)) {
    return 'video';
  }
  if (mime.startsWith('audio/') || ['mp3', 'wav', 'ogg', 'oga', 'm4a', 'aac', 'flac', 'weba'].includes(format)) {
    return 'audio';
  }
  if (mime === 'application/pdf' || format === 'pdf') {
    return 'pdf';
  }
  if (['docx', 'doc', 'rtf', 'odt'].includes(format) || mime.includes('word') || mime.includes('wordprocessingml')) {
    return 'docx';
  }
  if (['pptx', 'ppt', 'odp'].includes(format) || mime.includes('presentation') || mime.includes('powerpoint')) {
    return 'pptx';
  }
  if (['xlsx', 'xls', 'csv', 'ods'].includes(format) || mime.includes('spreadsheet') || mime.includes('excel') || mime === 'text/csv') {
    return 'spreadsheet';
  }
  if (
    mime.startsWith('text/') ||
    ['txt', 'md', 'json', 'xml', 'html', 'htm', 'js', 'jsx', 'ts', 'tsx', 'css', 'py', 'java', 'c', 'cpp', 'h', 'cs', 'sql', 'sh', 'yaml', 'yml'].includes(format)
  ) {
    return 'code';
  }
  return 'other';
};

export const canPreviewFile = (fileFormat, mimeType) => {
  if (!fileFormat && !mimeType) return false;
  const category = getFileCategory(fileFormat, mimeType);
  return category !== 'other' || ['pdf', 'doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx', 'csv', 'txt', 'mp4', 'mp3'].includes((fileFormat || '').toLowerCase());
};

export const isImageFile = (fileFormat, mimeType) => {
  return getFileCategory(fileFormat, mimeType) === 'image';
};

export const isVideoFile = (fileFormat, mimeType) => {
  return getFileCategory(fileFormat, mimeType) === 'video';
};

export const isAudioFile = (fileFormat, mimeType) => {
  return getFileCategory(fileFormat, mimeType) === 'audio';
};

export const isPdfFile = (fileFormat, mimeType) => {
  return getFileCategory(fileFormat, mimeType) === 'pdf';
};

export const isOfficeFile = (fileFormat, mimeType) => {
  const cat = getFileCategory(fileFormat, mimeType);
  return ['docx', 'pptx', 'spreadsheet'].includes(cat);
};

// ==========================================
// API Services
// ==========================================

// Auth services
export const authAPI = {
  registerTeacher: (data) => api.post('/auth/register/teacher', data),
  login: (data) => api.post('/auth/login', data),
  getCurrentUser: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
  updateAvatar: (formData) => {
    return api.put('/auth/update-avatar', formData, {
      headers: {
        'Content-Type': undefined,
      },
    });
  },
  updateSettings: (data) => api.put('/auth/update-settings', data),
  changePassword: (data) => api.put('/auth/change-password', data),
  logout: () => api.post('/auth/logout'),
};

// Teacher services
export const teacherAPI = {
  getDashboardStats: () => api.get('/teacher/dashboard/stats'),
  getProfile: () => api.get('/teacher/profile'),
  getMaterials: (params) => api.get('/teacher/materials', { params }),
  getMaterialById: (id) => api.get(`/teacher/materials/${id}`),
  uploadMaterial: (formData) => {
    return api.post('/teacher/materials/upload', formData, {
      headers: {
        'Content-Type': undefined,
      },
    });
  },
  updateMaterial: (id, data) => api.put(`/teacher/materials/${id}`, data),
  deleteMaterial: (id) => api.delete(`/teacher/materials/${id}`),
  trackDownload: (materialId) => api.post(`/teacher/materials/${materialId}/track-download`),
  getAnalytics: (params) => api.get('/teacher/analytics', { params }),
};

const getPersonalStoragePath = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  return user.role === 'school_admin' ? '/school-admin/personal-storage' : '/teacher/personal-storage';
};

// Personal Storage services
export const personalStorageAPI = {
  getMyFiles: (params) => api.get(getPersonalStoragePath(), { params }),
  uploadFile: (formData) => {
    return api.post(`${getPersonalStoragePath()}/upload`, formData, {
      headers: {
        'Content-Type': undefined,
      },
    });
  },
  downloadFile: (id) => {
    const token = localStorage.getItem('token');
    return `${API_URL}${getPersonalStoragePath()}/${id}/download?token=${encodeURIComponent(token || '')}`;
  },
  previewFile: (id) => {
    const token = localStorage.getItem('token');
    return `${API_URL}${getPersonalStoragePath()}/${id}/preview?token=${encodeURIComponent(token || '')}`;
  },
  deleteFile: (id) => api.delete(`${getPersonalStoragePath()}/${id}`),
  
  // Folders
  getFolders: (params) => api.get(`${getPersonalStoragePath()}/folders`, { params }),
  createFolder: (data) => api.post(`${getPersonalStoragePath()}/folders`, data),
  deleteFolder: (id) => api.delete(`${getPersonalStoragePath()}/folders/${id}`),
  
  // File Ops
  moveFile: (data) => api.post(`${getPersonalStoragePath()}/move`, data),
  copyFile: (id, data) => api.post(`${getPersonalStoragePath()}/copy/${id}`, data),
  updateFile: (id, formData) => {
    return api.put(`${getPersonalStoragePath()}/${id}`, formData, {
      headers: {
        'Content-Type': undefined,
      },
    });
  },
};

// Admin services
export const adminAPI = {
  getDashboardStats: () => api.get('/admin/dashboard/stats'),
  getPendingApprovals: (params) => api.get('/admin/approvals/pending', { params }),
  approveTeacher: (requestId, data) => api.post(`/admin/approve/${requestId}`, data),
  rejectTeacher: (requestId, data) => api.post(`/admin/reject/${requestId}`, data),
  getAllTeachers: (params) => api.get('/admin/teachers', { params }),
  getRejectedTeachers: (params) => api.get('/admin/rejected-teachers', { params }),
  getTeacher: (id) => api.get(`/admin/teachers/${id}`),
  getAccessManagedUsers: (params) => api.get('/admin/access/users', { params }),
  updateUserAccess: (id, data) => api.put(`/admin/access/users/${id}`, data),
  deleteTeacher: (id) => api.delete(`/admin/teachers/${id}`),
  toggleUserRestriction: (id, isRestricted) => api.put(`/admin/teachers/${id}/restrict`, { isRestricted }),
  updateTeacherAccess: (id, data) => api.put(`/admin/teachers/${id}/access`, data),
  resetUserPassword: (id, password) => api.post(`/admin/teachers/${id}/reset-password`, { password }),
  
   // School Admin Management
  getSchoolAdmins: async (params) => {
    const response = await api.get('/admin/school-admins', { params });
    return response; // Return full response
  },
  
  createSchoolAdmin: async (data) => {
    const response = await api.post('/admin/school-admins', data);
    return response; // Return full response with data.tempPassword
  },
  
  updateSchoolAdmin: async (id, data) => {
    const response = await api.put(`/admin/school-admins/${id}`, data);
    return response;
  },
  
  deleteSchoolAdmin: async (id) => {
    const response = await api.delete(`/admin/school-admins/${id}`);
    return response;
  },
  
  resetSchoolAdminPassword: async (id) => {
    const response = await api.post(`/admin/school-admins/${id}/reset-password`);
    return response; // Return full response with data.tempPassword
  },
  
  // Schools
  getSchools: (params) => api.get('/admin/schools', { params }),
  getSchoolById: (id) => api.get(`/admin/schools/${id}`),
  createSchool: (data) => api.post('/admin/schools', data),
  updateSchool: (id, data) => api.put(`/admin/schools/${id}`, data),
  deleteSchool: (id) => api.delete(`/admin/schools/${id}`),
  
  // Departments
  getDepartments: (params) => api.get('/admin/departments', { params }),
  createDepartment: (data) => api.post('/admin/departments', data),
  updateDepartment: (id, data) => api.put(`/admin/departments/${id}`, data),
  deleteDepartment: (id) => api.delete(`/admin/departments/${id}`),
  addDepartmentYear: (deptId, year) => api.post(`/admin/departments/${deptId}/years`, { year }),
  removeDepartmentYear: (deptId, year) => api.delete(`/admin/departments/${deptId}/years/${year}`),
  
  // Courses
  getCourses: (params) => api.get('/admin/courses', { params }),
  createCourse: (data) => api.post('/admin/courses', data),
  updateCourse: (id, data) => api.put(`/admin/courses/${id}`, data),
  deleteCourse: (id) => api.delete(`/admin/courses/${id}`),
  
  // Feedback
  getFeedback: (params) => api.get('/admin/feedback', { params }),
  updateFeedbackStatus: (id, status) => api.put(`/admin/feedback/${id}`, { status }),
  deleteFeedback: (id) => api.delete(`/admin/feedback/${id}`),
};

// School Admin services
export const schoolAdminAPI = {
  getDashboardStats: () => api.get('/school-admin/dashboard'),
  getTeachers: (params) => api.get('/school-admin/teachers', { params }),
  getPendingTeachers: (params) => api.get('/school-admin/teachers/pending', { params }),
  getDepartments: (params) => api.get('/school-admin/departments', { params }),
  getCourses: (params) => api.get('/school-admin/courses', { params }),
  approveTeacher: (teacherId, data) => api.post(`/school-admin/teachers/${teacherId}/approve`, data),
  rejectTeacher: (teacherId, data) => api.post(`/school-admin/teachers/${teacherId}/reject`, data),
  updateTeacher: (id, data) => api.put(`/school-admin/teachers/${id}`, data),
  deleteTeacher: (id) => api.delete(`/school-admin/teachers/${id}`),
  toggleUserRestriction: (id, isRestricted) => api.put(`/school-admin/teachers/${id}/restrict`, { isRestricted }),
  updateTeacherAccess: (id, data) => api.put(`/school-admin/teachers/${id}/access`, data),
  resetUserPassword: (id, password) => api.post(`/school-admin/teachers/${id}/reset-password`, { password }),
  createDepartment: (data) => api.post('/school-admin/departments', data),
  updateDepartment: (id, data) => api.put(`/school-admin/departments/${id}`, data),
  deleteDepartment: (id) => api.delete(`/school-admin/departments/${id}`),
  createCourse: (data) => api.post('/school-admin/courses', data),
  updateCourse: (id, data) => api.put(`/school-admin/courses/${id}`, data),
  deleteCourse: (id) => api.delete(`/school-admin/courses/${id}`),
  addDepartmentYear: (deptId, year) => api.post(`/school-admin/departments/${deptId}/years`, { year }),
  removeDepartmentYear: (deptId, year) => api.delete(`/school-admin/departments/${deptId}/years/${year}`),
  
  // Feedback
  getFeedback: (params) => api.get('/school-admin/feedback', { params }),
  updateFeedbackStatus: (id, status) => api.put(`/school-admin/feedback/${id}/status`, { status }),
  deleteFeedback: (id) => api.delete(`/school-admin/feedback/${id}`),
};

// Public services
export const publicAPI = {
  getSchools: (params) => api.get('/public/schools', { params }),
  getSchoolById: (id) => api.get(`/public/schools/${id}`),
  getSchoolDepartments: (schoolId) => api.get(`/public/schools/${schoolId}/departments`),
  getDepartmentById: (id) => api.get(`/public/departments/${id}`),
  getCourses: (params) => api.get('/public/courses', { params }),
  getCourseById: (id) => api.get(`/public/courses/${id}`),
  getMaterials: (params) => api.get('/public/materials', { params }),
  getMaterialById: (id) => api.get(`/public/materials/${id}`),
  getSchoolMaterials: (schoolId, params) => api.get(`/public/schools/${schoolId}/materials`, { params }),
  getDownloadUrl: (materialId) => `${API_URL}/public/materials/download/${materialId}`,
  getPreviewUrl: (materialId) => `${API_URL}/public/materials/preview/${materialId}`,
  getFullImageUrl: (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    let host;
    try {
      const baseUrlObject = new URL(API_URL);
      host = `${baseUrlObject.protocol}//${baseUrlObject.host}`;
    } catch (e) {
      host = '';
    }
    if (url.startsWith('/api')) return `${host}${url}`;
    return `${API_URL}${url}`;
  },
  canPreview: (material) => {
    if (!material?.file) return false;
    return canPreviewFile(material.file.format, material.file.mimeType);
  },
  isImage: (material) => {
    if (!material?.file) return false;
    return isImageFile(material.file.format, material.file.mimeType);
  },
  isVideo: (material) => {
    if (!material?.file) return false;
    return isVideoFile(material.file.format, material.file.mimeType);
  },
  isAudio: (material) => {
    if (!material?.file) return false;
    return isAudioFile(material.file.format, material.file.mimeType);
  },
  isPdf: (material) => {
    if (!material?.file) return false;
    return isPdfFile(material.file.format, material.file.mimeType);
  },
  isOffice: (material) => {
    if (!material?.file) return false;
    return isOfficeFile(material.file.format, material.file.mimeType);
  },
  getFileCategory: (material) => {
    if (!material?.file) return 'other';
    return getFileCategory(material.file.format, material.file.mimeType);
  },
  trackDownload: (materialId) => api.post(`/public/materials/${materialId}/track-download`),
  getRecentMaterials: (params) => api.get('/public/materials', { params: { limit: params?.limit || 12, sort: '-createdAt', ...params } }),
  getPopularMaterials: (params) => api.get('/public/materials', { params: { limit: params?.limit || 12, sort: '-downloads', ...params } }),
  searchMaterials: (params) => api.get('/public/materials', { params }),
  getPlatformStats: () => api.get('/public/stats'),
  getPlatformFeatures: () => api.get('/public/features'),
  submitFeedback: (data) => api.post('/public/feedback', data),
  subscribeNewsletter: (email) => api.post('/public/subscribe', { email }),
};

export const chatAPI = {
  getContacts: () => api.get('/chat/contacts'),
  getMessages: (contactId) => api.get(`/chat/messages/${contactId}`),
  sendMessage: (data) => api.post('/chat/send', data),
  markAsRead: (contactId) => api.post(`/chat/mark-read/${contactId}`),
};

export { api };
export default api;